import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

import * as api from '~services/api';
import { Gap, Checkbox, Tags, FormLabel as Label } from '~ui-kit';
import { errorColor } from '../../../../common/theme';
import { addToast } from '~features/toast/ducks';

import { useMunicipalityOptions, useOfficeOptions, useSpaces } from '~hooks';

// Actions
import { saveSpaceNickName } from '~actions/transferActions';

import { getUser } from '~features/user/ducks';

import { getBuildings, formatSpaceOption, groupSpacesByOffice } from './utils';

const additionalInfoLabel =
  'En löydä huonetta yksiköllä, rakennuksella enkä tilatunnuksella. Tarkennan tiedot palvelupyynnössä.';

// eslint-disable-next-line react/display-name
const noOptionsMessage = label => props =>
  (
    <components.NoOptionsMessage {...props}>
      {label || 'Ei hakutuloksia'}
    </components.NoOptionsMessage>
  );

const SpaceSelect = props => {
  const dispatch = useDispatch();
  const user = useSelector(state => getUser(state));
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedBuildingFloor, setSelectedBuildingFloor] = useState(null);
  const [spaceNamingAllowed, setSpaceNamingAllowed] = useState(false);
  const [spacesInFloor, setSpacesInFloor] = useState([]);
  const [spaceNickname, setSpaceNickname] = useState('');
  const [searchType, setSearchType] = useState('unit');
  const [unknownRoom, setUnknownRoom] = useState(false);
  const [office, setOfficeState] = useState(null);
  const [municipality, setMunicipalityState] = useState(null);
  const [collapse, setCollapse] = useState(false);
  const [hideUnitSearchType, setHideUnitSearchType] = useState(false);

  const municipalityOptions = useMunicipalityOptions();
  const offices = useSelector(state => state.reducer.offices);
  const officeOptions = useOfficeOptions(municipality);

  const userOrganizationId = user?.ordererOrganization.id;
  const userOfficeId = user?.ordererOrganization.officeId;
  const userOffice = offices.find(office => office.id === userOfficeId);

  const {
    onChange,
    formId,
    value,
    meta,
    handleBlur,
    name,
    onlyRoom,
    onlyBuilding,
    required,
    label,
    hideLabel,
    info,
    collapseType,
    disableCollapse,
    externalQRCodeReading,
  } = props;

  const setMunicipality = municipality => {
    setMunicipalityState(municipality);
    setOfficeState(null);
    setUnknownRoom(false);
    setSelectedBuilding(null);
    setSelectedBuildingFloor(null);
    onChange(null);
  };

  const setOffice = office => {
    setOfficeState(office);
    setUnknownRoom(false);
    setSelectedBuilding(null);
    setSelectedBuildingFloor(null);
    onChange(null);
  };

  const { spaces: ownUnitSpaces, loading: ownUnitSpacesLoading } =
    useSpaces(userOrganizationId);

  const groupedOwnUnitSpaceOptions = groupSpacesByOffice(
    ownUnitSpaces,
    unknownRoom
  );

  // Check if user's unit has spaces. If it doesn't, hide that search type
  useEffect(() => {
    if (!ownUnitSpacesLoading && ownUnitSpaces.length === 0) {
      setHideUnitSearchType(true);
      setSearchType('building');
    }
  }, [ownUnitSpaces, ownUnitSpacesLoading]);

  // Autofill user's municipality and office for building select
  useEffect(() => {
    const update = async () => {
      if (
        offices.length &&
        municipalityOptions.length &&
        !municipality &&
        !office &&
        userOffice
      ) {
        setMunicipalityState({
          value: userOffice.municipalityCode,
          label: userOffice.municipalityName,
        });

        setOfficeState({
          value: userOffice.id,
          label: userOffice.name,
        });
      }
    };

    update();
  }, [municipality, office, offices, municipalityOptions, userOffice]);

  const getBuildingOptions = async text => {
    const buildings = await getBuildings(office, text);
    return buildings;
  };

  const getRoomOptions = (text = '') => {
    if (text.length < 3) return Promise.resolve([]);

    const code = encodeURIComponent(`${text}`);

    let queryParameters = `?code=${code}`;
    if (formId) queryParameters += `&forForm=${formId}`;
    if (office) queryParameters += `&officeId=${office.value}`;

    return api
      .get(`spaces/rooms/find${queryParameters}`)
      .then((spaces = []) => groupSpacesByOffice(spaces, unknownRoom))
      .catch(() => Promise.resolve([]));
  };

  const setBuildingFloor = async (floor, optionalBuilding) => {
    // optionalBuilding is fresh data that hasn't yet been synced to state
    const buildingData = optionalBuilding || selectedBuilding;

    if (floor && buildingData) {
      let { hospital, office, building } = buildingData;

      hospital = encodeURIComponent(hospital);
      office = encodeURIComponent(office);
      building = encodeURIComponent(building);
      const floorURI = encodeURIComponent(floor.value);

      onChange(null); // Reset selected space

      const spacesInFloor = await api.get(
        `spaces/rooms/find?hospital=${hospital}&office=${office}&building=${building}&floor=${floorURI}`
      );
      setSpacesInFloor(spacesInFloor);
    }

    setSelectedBuildingFloor(floor);
  };

  const setBuilding = building => {
    setSelectedBuilding(building);
    setBuildingFloor(
      building?.floors?.length ? building.floors[0] : null,
      building
    );
    setUnknownRoom(false);
    onChange(null);
  };

  const setSearchTypeFun = type => {
    onChange(null); // Reset selected space
    setBuilding(null);
    setBuildingFloor(null);
    setSearchType(type);
    setUnknownRoom(false);
  };

  const setSpace = option => {
    setSpaceNamingAllowed(false);

    if (option) {
      const selectedSpace = JSON.parse(option.value);
      return onChange(selectedSpace);
    }
    return onChange(null); // Reset selected space
  };

  const handleQRCode = async text => {
    const code = encodeURIComponent(text);
    try {
      const spaces = await api.get(`organizations/spaces/find?code=${code}`);

      if (spaces && spaces[0]) {
        const space = spaces[0];
        setSearchTypeFun('room');
        onChange(space);
      } else {
        dispatch(
          addToast({ message: 'QR-koodilla ei löytynyt tilaa', type: 'ERROR' })
        );
        console.log(`Space not found from QR code (${text})`);
      }
    } catch (e) {
      dispatch(
        addToast({ message: 'QR-koodilla haku epäonnistui', type: 'ERROR' })
      );
      console.log(`Failed to fetch space (${text})`);
    }
  };

  // Autofill space if external QR code is provided
  useEffect(() => {
    if (externalQRCodeReading) {
      handleQRCode(externalQRCodeReading);
    }
  }, [externalQRCodeReading]);

  const toggleUnknownRoom = newSpace => {
    if (unknownRoom) {
      setUnknownRoom(false);
      onChange(null);
    } else {
      setUnknownRoom(true);
      onChange(newSpace || {});
    }
  };

  const saveSpaceNicknameFun = () => {
    const selectedSpace = value;

    dispatch(
      saveSpaceNickName({
        orgId: userOrganizationId,
        nickname: spaceNickname,
        space: selectedSpace,
      })
    );

    onChange({
      ...selectedSpace,
      nickname: spaceNickname,
    });

    setSpaceNamingAllowed(false);
    setSpaceNickname('');
  };

  const getSelectStyles = () => {
    const controlStyles = {
      fontSize: '16px',
    };

    const menuStyles = {
      fontSize: '16px',
    };

    const singleValueStyles = {
      overflow: 'visible',
    };

    const groupHeadingStyles = {
      fontWeight: 'bold',
      color: 'unset',
    };

    // This is applied to all selects
    const selectProps = {
      onBlur: handleBlur,
      onBlurResetsInput: false,
      styles: {
        control: provided => ({ ...provided, ...controlStyles }),
        menu: provided => ({ ...provided, ...menuStyles }),
        singleValue: provided => ({ ...provided, ...singleValueStyles }),
        groupHeading: provided => ({ ...provided, ...groupHeadingStyles }),
      },
      components: { NoOptionsMessage: noOptionsMessage() },
    };

    // This is for the actual space select field (for error warnings)
    const spaceSelectProps = {
      styles: {
        ...selectProps.styles,
        control: provided =>
          meta.touched && meta.error && !unknownRoom && errorColor
            ? {
                ...provided,
                ...controlStyles,
                borderColor: errorColor,
                '&:hover': {
                  borderColor: errorColor,
                },
              }
            : { ...provided, ...controlStyles },
      },
      isClearable: true,
    };

    return { controlStyles, selectProps, spaceSelectProps };
  };

  const renderSearch = type => {
    const { controlStyles, selectProps, spaceSelectProps } = getSelectStyles();

    // react-select v2 requires the value to be an array for whatever reason
    const spaceValue = [formatSpaceOption(value, unknownRoom)];

    switch (type) {
      case 'room':
        return (
          <AsyncSelect
            {...selectProps}
            {...spaceSelectProps}
            key={office?.value} // Reset value when changing office
            value={spaceValue}
            loadOptions={getRoomOptions}
            name={name}
            isDisabled={unknownRoom}
            onChange={option => setSpace(option)}
            placeholder="Etsi huoneen nimellä tai koodilla"
            components={{
              NoOptionsMessage: noOptionsMessage(
                'Hakutermiä vastaavaa huonetta ei löytynyt'
              ),
            }}
          />
        );
      case 'building':
        return (
          <BuildingTable>
            <tbody>
              <tr>
                <td>Kunta</td>
                <td>
                  <Select
                    {...selectProps}
                    placeholder="Valitse..."
                    value={municipality}
                    onChange={setMunicipality}
                    options={municipalityOptions}
                  />
                </td>
              </tr>

              <tr>
                <td>Toimipiste</td>
                <td>
                  <Select
                    {...selectProps}
                    placeholder="Valitse..."
                    value={office}
                    onChange={setOffice}
                    options={officeOptions}
                  />
                </td>
              </tr>

              <tr>
                <td>Rakennus</td>
                <td>
                  <AsyncSelect
                    {...selectProps}
                    key={office?.value} // Reset value when changing office
                    name="building"
                    placeholder="Etsi rakennusta"
                    value={selectedBuilding}
                    loadOptions={getBuildingOptions}
                    defaultOptions
                    isDisabled={unknownRoom}
                    onChange={value => setBuilding(value)}
                    styles={{
                      control: provided =>
                        meta.touched && meta.error && unknownRoom && errorColor
                          ? {
                              ...provided,
                              ...controlStyles,
                              borderColor: errorColor,
                              '&:hover': {
                                borderColor: errorColor,
                              },
                            }
                          : { ...provided, ...controlStyles },
                    }}
                  />
                </td>
              </tr>

              <tr>
                <td>Kerros</td>
                <td>
                  <Select
                    {...selectProps}
                    {...spaceSelectProps}
                    placeholder=""
                    name="floor"
                    options={selectedBuilding?.floors}
                    value={selectedBuildingFloor}
                    onChange={value => {
                      setBuildingFloor(value);
                      setUnknownRoom(false);
                    }}
                    isDisabled={!selectedBuilding || unknownRoom}
                    isClearable={false}
                    isSearchable={false}
                    styles={{
                      control: provided =>
                        meta.touched && meta.error && unknownRoom && errorColor
                          ? {
                              ...provided,
                              ...controlStyles,
                              borderColor: errorColor,
                              '&:hover': {
                                borderColor: errorColor,
                              },
                            }
                          : { ...provided, ...controlStyles },
                    }}
                  />
                </td>
              </tr>
              <tr>
                <td>Tilan nimi</td>
                <td>
                  <Select
                    {...selectProps}
                    {...spaceSelectProps}
                    value={spaceValue}
                    options={spacesInFloor.map(x =>
                      formatSpaceOption(x, unknownRoom)
                    )}
                    name={name}
                    onChange={option => setSpace(option)}
                    placeholder="Etsi tilatunnuksella tai nimellä"
                    components={{
                      NoOptionsMessage: noOptionsMessage(
                        'Ei tiloja valitussa kerroksessa'
                      ),
                    }}
                    isDisabled={!selectedBuilding || unknownRoom}
                  />
                </td>
              </tr>
            </tbody>
          </BuildingTable>
        );
      case 'unit':
      default:
        return (
          <UnitSearch>
            <Select
              {...selectProps}
              {...spaceSelectProps}
              value={spaceValue}
              options={groupedOwnUnitSpaceOptions}
              name={name}
              onChange={option => setSpace(option)}
              placeholder="Rajaa toimipisteellä, osoitteella tai nimellä"
              components={{
                NoOptionsMessage: noOptionsMessage(
                  'Ei tiloja valitulla yksiköllä'
                ),
              }}
              isDisabled={unknownRoom}
            />

            {!unknownRoom && value && (
              <NameSpace>
                <Checkbox
                  label="Anna huoneelle nimi"
                  value={spaceNamingAllowed}
                  onChange={() => {
                    setSpaceNamingAllowed(!spaceNamingAllowed);
                    setSpaceNickname('');
                  }}
                >
                  {spaceNamingAllowed && (
                    <SpaceNamingForm>
                      <input
                        className="form-control"
                        placeholder="Uusi nimi"
                        maxLength="30"
                        value={spaceNickname}
                        onChange={({ target }) =>
                          setSpaceNickname(target.value)
                        }
                      />
                      <SaveNamedSpace
                        type="SaveNamedSpace"
                        onClick={saveSpaceNicknameFun}
                      >
                        Tallenna
                      </SaveNamedSpace>
                    </SpaceNamingForm>
                  )}
                </Checkbox>
              </NameSpace>
            )}
          </UnitSearch>
        );
    }
  };

  const renderCollapse = collapseType => {
    switch (collapseType) {
      case 'phone':
        return (
          <p>
            En löydä huonetta yksiköllä, rakennuksella enkä tilatunnuksella.
            Soita tilapalveluiden help-deskiin, numero{' '}
            <a href="tel:0331159416">0331159416</a>.
          </p>
        );
      case 'feedback':
        return (
          <p>
            En löydä huonetta yksiköllä, rakennuksella enkä tilatunnuksella.
            Ilmoita asiasta <Link to="/palaute">yhteydenottokaavakkeella</Link>.
          </p>
        );
      default:
        return (
          <Checkbox
            onChange={() => toggleUnknownRoom()}
            value={unknownRoom}
            label={additionalInfoLabel}
          />
        );
    }
  };

  let augSearchType = searchType;
  if (onlyRoom) {
    augSearchType = 'room';
  } else if (onlyBuilding) {
    augSearchType = 'building';
  }

  const options = !onlyRoom && !onlyBuilding;

  const searchTypes = [
    { value: 'building', label: 'Rakennus' },
    { value: 'room', label: 'Tilatunnus' },
  ];

  if (!hideUnitSearchType)
    searchTypes.unshift({ value: 'unit', label: 'Oma yksikkö' });

  return (
    <Container>
      {!hideLabel && label && (
        <Label required={required} info={info}>
          {label}
        </Label>
      )}
      <FormGroup>
        {options && (
          <TagWrapper>
            <Tags
              options={searchTypes}
              value={augSearchType}
              onChange={value => setSearchTypeFun(value)}
            />
          </TagWrapper>
        )}

        {renderSearch(augSearchType)}

        {!disableCollapse && (
          <CollapseWrapper>
            <Gap height="16px" />

            <CollapseHeader onClick={() => setCollapse(!collapse)}>
              <Chevron icon={faChevronDown} collapse={collapse} />
              En löydä tilaani
            </CollapseHeader>
            {collapse && <Collapse>{renderCollapse(collapseType)}</Collapse>}
          </CollapseWrapper>
        )}
      </FormGroup>
    </Container>
  );
};

const Container = styled.div``;

const FormGroup = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  max-width: 550px;

  background-color: #f2f2f2;
`;

const TagWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 12px;
  color: var(--primaryColor);
`;

const UnitSearch = styled.div``;

const NameSpace = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
`;

const SaveNamedSpace = styled.button`
  background-color: #3d965a;
  color: #fff;
  padding: 8px 24px;
  margin-left: 16px;
  border: none;
  border-radius: 3px;
`;

const SpaceNamingForm = styled.div`
  display: flex;
  margin-top: 5px;
`;

const CollapseHeader = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--primaryColor);
  gap: 12px;
  font-size: 16px;
`;

const Collapse = styled.div`
  padding-top: 12px;
  color: var(--primaryColor);
`;

const Chevron = styled(FontAwesomeIcon)`
  font-size: 20px;
  transform: rotate(${props => (props.collapse ? '180deg' : '0deg')});
  transition: transform 0.15s;
`;

const BuildingTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 12px;

  tr > td:first-child {
    color: var(--primaryColor);
    font-size: 16px;
  }

  // Gets rid of annoying layout jumping
  tr > td:last-child {
    max-width: 280px;

    // I don't fully understand why this fixes mobile scaling
    @media (max-width: 768px) {
      max-width: 180px;
    }
  }
`;

const CollapseWrapper = styled.div`
  padding: 0 16px;
`;

export default SpaceSelect;
